// The DM header's presence: the partner's device glyph (tinted = connected now, muted = last device),
// the «En línea» / «Últ. vez …» line, and the per-device drill-in
// (brief 2026-10-16 §B/§D/§E). Plus this tab's own desktop heartbeat.
//
// Everything paints from the store (worker `dmPresence`); the plugin poll runs beside it every 30 s
// and never blocks a frame. The relative text re-ticks every minute (`useMinuteClock`).
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { data } from '../../../bridge/client';
import { isTwelveHour } from '../../../core/clockStyle';
import { DeviceIndicatorPresentation, type DeviceRow } from '../../../core/deviceIndicatorPresentation';
import { LastSeenPresentation } from '../../../core/lastSeenPresentation';
import { localTimeZone } from '../../../core/time';
import type { DmPresence } from '../../../data/features/devicePresence';
import { Icon } from '../../../design/icons';
import { t } from '../../../i18n/i18n';
import { useLive, useMinuteClock } from '../../hooks';
import './presence.css';

const EMPTY: DmPresence = { status: null, lastActivityAt: 0, indicator: null, isBot: false };

/** The partner's presence from the store, re-asked from the server every 30 s while the DM is open. */
export function useDmPresence(userId: string | null): DmPresence {
  const { value } = useLive<DmPresence>(() => (userId ? data.call('dmPresence', userId) : Promise.resolve(EMPTY)), ['userStatuses', 'syncState'], [userId]);
  useEffect(() => {
    if (!userId) return;
    const refresh = () => {
      if (typeof document === 'undefined' || document.visibilityState === 'visible') void data.call('refreshDmPresence', userId);
    };
    refresh();
    const id = setInterval(refresh, DeviceIndicatorPresentation.pollIntervalMs);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [userId]);
  // A live `status_change` to offline carries no time: re-ask at once so «Últ. vez» is fresh, not
  // the activity stored the last time the person was polled.
  const lastStatus = useRef<string | null>(null);
  const status = value?.status ?? null;
  useEffect(() => {
    if (userId && status === 'offline' && lastStatus.current && lastStatus.current !== 'offline') void data.call('refreshDmPresence', userId);
    lastStatus.current = status;
  }, [userId, status]);
  return value ?? EMPTY;
}

function glyphIcon(glyph: ReturnType<typeof DeviceIndicatorPresentation.glyph>): 'desktop' | 'mobile' | 'devices' | null {
  return glyph === 'both' ? 'devices' : glyph === 'desktop' ? 'desktop' : glyph === 'mobile' ? 'mobile' : null;
}

/**
 * The header's title-row glyph + its subtitle line, both opening the drill-in. `username` is the
 * partner's handle; `noPhrase` = a bot / integration / self-DM (no devices: just the handle).
 */
export function useDmPresenceHeader(userId: string, username: string, noPhrase: boolean, partnerViewing = false): { glyph: ReactNode; subtitle: ReactNode } {
  const presence = useDmPresence(noPhrase ? null : userId);
  const now = useMinuteClock();
  const [open, setOpen] = useState(false);
  const opener = useRef<HTMLButtonElement>(null);
  const glyphButton = useRef<HTMLButtonElement>(null);
  const pop = useRef<HTMLDivElement>(null);
  const skip = noPhrase || presence.isBot;
  // «Viendo el chat» (shared/API.md §39) outranks «En línea»; «Últ. vez» rules otherwise unchanged.
  const viewing = !noPhrase && partnerViewing;
  const phrase = viewing ? t('web.viewing.dm') : skip ? null : LastSeenPresentation.subtitle(presence.status, presence.lastActivityAt, presence.indicator, now, isTwelveHour(), localTimeZone());
  const line = LastSeenPresentation.headerLine(phrase, username);
  const glyph = skip ? 'none' : DeviceIndicatorPresentation.glyph(presence.indicator);
  const icon = glyphIcon(glyph);
  const live = DeviceIndicatorPresentation.isAnyActive(presence.indicator);
  const canOpen = !skip && !!presence.indicator;

  useEffect(() => {
    if (!open) return;
    const down = (e: MouseEvent) => {
      const target = e.target as Node;
      // Spare the openers: a click on them toggles, it must not close-then-reopen.
      if (pop.current?.contains(target) || opener.current?.contains(target) || glyphButton.current?.contains(target)) return;
      setOpen(false);
    };
    const key = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', down);
    document.addEventListener('keydown', key);
    return () => {
      document.removeEventListener('mousedown', down);
      document.removeEventListener('keydown', key);
    };
  }, [open]);
  useEffect(() => setOpen(false), [userId]);

  return {
    glyph: icon ? (
      <button
        ref={glyphButton}
        type="button"
        className={`device-glyph${live ? ' live' : ''}`}
        onClick={() => canOpen && setOpen((o) => !o)}
        title={t('web.device.show')}
        aria-label={t('web.device.show')}
        aria-expanded={open}
      >
        <Icon name={icon} size={15} />
      </button>
    ) : null,
    subtitle: (
      <span className="dm-presence">
        {canOpen ? (
          <button ref={opener} type="button" className={`subtitle dm-presence-line${viewing ? ' viewing-now' : ''}`} onClick={() => setOpen((o) => !o)} aria-expanded={open} title={line}>
            {line}
          </button>
        ) : (
          <span className={`subtitle${viewing ? ' viewing-now' : ''}`} title={line}>
            {line}
          </span>
        )}
        {open && presence.indicator ? (
          <div ref={pop} className="device-popover" role="dialog" aria-label={t('profile.devices')}>
            <div className="device-popover-title">{t('profile.devices')}</div>
            {DeviceIndicatorPresentation.rows(presence.indicator).map((row) => (
              <DeviceRowView key={row.kind} row={row} now={now} />
            ))}
          </div>
        ) : null}
      </span>
    ),
  };
}

function DeviceRowView({ row, now }: { row: DeviceRow; now: number }) {
  return (
    <div className={`device-row${row.isActive ? ' live' : ''}`}>
      <Icon name={row.kind === 'desktop' ? 'desktop' : 'mobile'} size={16} />
      <span className="device-name">{t(row.kind === 'desktop' ? 'profile.device.desktop' : 'profile.device.mobile')}</span>
      <span className="device-when">{LastSeenPresentation.deviceRowText(row.isActive, row.lastSeenAt, now, isTwelveHour(), localTimeZone())}</span>
    </div>
  );
}

/**
 * This tab's desktop heartbeat (shared/PLUGINS.md §8.1, the webapp's cadence): every 30 s while the
 * tab is visible and someone used it in the last 5 minutes (Mattermost's default away timeout);
 * at most one per 15 s; fire-and-forget (the worker logs a failure once).
 */
export function useDesktopHeartbeat(enabled: boolean): void {
  useEffect(() => {
    if (!enabled) return;
    const INTERVAL = 30_000;
    const COOLDOWN = 15_000;
    const IDLE = 5 * 60_000;
    let lastInput = Date.now();
    let lastSent = 0;
    const beat = () => {
      const now = Date.now();
      if (document.visibilityState !== 'visible' || now - lastInput > IDLE || now - lastSent < COOLDOWN) return;
      lastSent = now;
      void data.call('desktopHeartbeat');
    };
    const input = () => {
      const wasIdle = Date.now() - lastInput > IDLE;
      lastInput = Date.now();
      if (wasIdle) beat();
    };
    const visible = () => {
      if (document.visibilityState === 'visible') {
        lastInput = Date.now();
        beat();
      }
    };
    beat();
    const id = setInterval(beat, INTERVAL);
    const events = ['pointerdown', 'keydown', 'wheel', 'touchstart', 'focus'] as const;
    for (const e of events) window.addEventListener(e, input, { passive: true, capture: true });
    document.addEventListener('visibilitychange', visible);
    return () => {
      clearInterval(id);
      for (const e of events) window.removeEventListener(e, input, { capture: true });
      document.removeEventListener('visibilitychange', visible);
    };
  }, [enabled]);
}
